import {
  Grid,
  Box,
  Typography,
  TextField,
  Pagination,
  CircularProgress,
  Alert,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useShippingZoneCommunes } from "../hooks/useShippingZoneCommunes";

/**
 * Renders the communes assigned to a specific shipping zone in a table
 * Columns adapt dynamically based on the country code
 *
 * @param {Object} props
 * @param {number} props.zoneId - The ID of the shipping zone to fetch communes for
 * @param {string} props.countryCode - ISO2 country code (e.g., 'CL' or 'MX') to set table headers
 */
export const ShippingZoneCommunes = ({ zoneId, countryCode = "CL" }) => {
  const {
    communes,
    loading,
    error,
    search,
    totalCount,
    pagination,
    handleSearchChange,
    handleChangePage,
  } = useShippingZoneCommunes(zoneId);

  const totalPages = Math.ceil(totalCount / pagination.rowsPerPage);

  const handlePageChange = (event, value) => {
    handleChangePage(value);
  };

  const renderTableHeaders = () => {
    if (countryCode === "MX") {
      return (
        <TableRow sx={{ backgroundColor: "#f9fafb" }}>
          <TableCell sx={{ fontWeight: "bold" }}>Comuna</TableCell>
          <TableCell sx={{ fontWeight: "bold" }}>Código Postal</TableCell>
          <TableCell sx={{ fontWeight: "bold" }}>Asentamiento</TableCell>
          <TableCell sx={{ fontWeight: "bold" }}>Región</TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow sx={{ backgroundColor: "#f9fafb" }}>
        <TableCell sx={{ fontWeight: "bold" }}>Comuna</TableCell>
        <TableCell sx={{ fontWeight: "bold" }}>Región</TableCell>
        <TableCell sx={{ fontWeight: "bold" }}>Ubigeo Code</TableCell>
      </TableRow>
    );
  };

  /**
   * Helper function to render table cells dynamically based on the country code.
   * @param {Object} commune - The commune data object
   */
  const renderTableCells = (commune) => {
    if (countryCode === "MX") {
      return (
        <>
          <TableCell component="th" scope="row">
            {commune.name}
          </TableCell>
          <TableCell component="th" scope="row">
            {commune.postal_code || "-"}
          </TableCell>
          <TableCell component="th" scope="row">
            {commune.settlement || "-"}
          </TableCell>
          <TableCell component="th" scope="row">
            {commune.region_name || "-"}
          </TableCell>
        </>
      );
    }

    return (
      <>
        <TableCell component="th" scope="row">
          {commune.name}
        </TableCell>
        <TableCell component="th" scope="row">
          {commune.region_name || "-"}
        </TableCell>
        <TableCell component="th" scope="row">
          {commune.ubigeo_code || "-"}
        </TableCell>
      </>
    );
  };

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h4" gutterBottom sx={{ mb: "1.5rem !important" }}>
        Comunas ({totalCount})
      </Typography>

      <TextField
        variant="outlined"
        size="small"
        placeholder="Buscar por nombre, código postal..."
        value={search}
        onChange={handleSearchChange}
        sx={{ width: 300 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={40} sx={{ color: "#14b8a6" }} />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table
              aria-label="tabla de Coberturas Zonas de despacho"
              className="fixed-table"
            >
              <TableHead sx={{ backgroundColor: "#fff" }}>
                {renderTableHeaders()}
              </TableHead>
              <TableBody>
                {communes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No se encontraron comunas que coincidan con la búsqueda.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  communes.map((commune) => (
                    <TableRow key={commune.id} hover>
                      {renderTableCells(commune)}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalCount > 0 && (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              pt={1}
            >
              <Typography variant="body2" color="text.secondary">
                Página {pagination.page} de {totalPages || 1}
              </Typography>

              <Pagination
                count={totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: "#14b8a6",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#0d9488",
                    },
                  },
                }}
              />
            </Box>
          )}
        </>
      )}
    </>
  );
};
